import { DialogComponent } from '@theme/dialog';

/**
 * A popup section component that extends DialogComponent with timer-based triggers
 * and visitor tracking functionality.
 *
 * @extends DialogComponent
 */
export class PopupSectionComponent extends DialogComponent {
  /** @type {number|null} */
  #timerTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    // In edit mode, always show the popup immediately and bypass all filters
    if (this.dataset.editMode === 'true') {
      this.showDialog();
      return;
    }

    // Mark this visitor in localStorage for "recent visitor" tracking
    this.#markVisitor();

    // Check if popup should be shown
    if (!this.#shouldShow()) return;

    // Set up timer-based auto-open if enabled
    const triggerType = this.dataset.triggerType;
    if (triggerType === 'timer' || triggerType === 'both') {
      const delay = parseInt(this.dataset.delay, 10) || 0;
      this.#timerTimeout = setTimeout(() => {
        this.showDialog();
        this.#markAsShown();
      }, delay * 1000);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clear timer if component is removed
    if (this.#timerTimeout) {
      clearTimeout(this.#timerTimeout);
      this.#timerTimeout = null;
    }
  }

  /**
   * Override showDialog to mark as shown when manually triggered
   * (unless in edit mode)
   */
  showDialog() {
    super.showDialog();
    // Don't mark as shown in edit mode (so it persists)
    if (this.dataset.editMode !== 'true') {
      this.#markAsShown();
    }
  }

  /**
   * Determines if the popup should be shown based on various conditions
   * @returns {boolean}
   */
  #shouldShow() {
    const sectionId = this.dataset.sectionId;

    // 1. Check "don't show again" dismissal (localStorage)
    if (this.#isDismissed(sectionId)) {
      return false;
    }

    // 2. Check "shown this session" (sessionStorage)
    if (this.dataset.showOncePerSession === 'true' && this.#wasShownThisSession(sectionId)) {
      return false;
    }

    // 3. Check logged-in user setting
    const isLoggedIn = this.dataset.isLoggedIn === 'true';
    const showToLoggedIn = this.dataset.showToLoggedIn === 'true';
    if (isLoggedIn && !showToLoggedIn) {
      return false;
    }

    // 4. Check recent visitor setting
    const showToRecent = this.dataset.showToRecent === 'true';
    if (!showToRecent && this.#isRecentVisitor()) {
      return false;
    }

    return true;
  }

  /**
   * Checks if the popup was dismissed via "don't show again"
   * @param {string} sectionId
   * @returns {boolean}
   */
  #isDismissed(sectionId) {
    try {
      const dismissedData = localStorage.getItem(`popup-dismissed-${sectionId}`);
      if (!dismissedData) return false;

      const { expiry } = JSON.parse(dismissedData);
      if (Date.now() > expiry) {
        // Dismissal has expired, remove it
        localStorage.removeItem(`popup-dismissed-${sectionId}`);
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Checks if the popup was already shown this session
   * @param {string} sectionId
   * @returns {boolean}
   */
  #wasShownThisSession(sectionId) {
    try {
      return sessionStorage.getItem(`popup-shown-${sectionId}`) === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Checks if the current visitor is a "recent" visitor
   * @returns {boolean}
   */
  #isRecentVisitor() {
    try {
      const visitorTimestamp = localStorage.getItem('popup-visitor-timestamp');
      if (!visitorTimestamp) return false;

      const recentDays = parseInt(this.dataset.recentVisitorDays, 10) || 7;
      const recentThreshold = recentDays * 24 * 60 * 60 * 1000; // Convert days to ms
      const timeSinceFirstVisit = Date.now() - parseInt(visitorTimestamp, 10);

      // They're a recent visitor if they visited within the threshold
      // but NOT if this is their first visit (timestamp was just set)
      return timeSinceFirstVisit > 1000 && timeSinceFirstVisit < recentThreshold;
    } catch {
      return false;
    }
  }

  /**
   * Marks the current visitor's first visit timestamp
   */
  #markVisitor() {
    try {
      if (!localStorage.getItem('popup-visitor-timestamp')) {
        localStorage.setItem('popup-visitor-timestamp', Date.now().toString());
      }
    } catch {
      // localStorage not available
    }
  }

  /**
   * Marks the popup as shown for this session
   */
  #markAsShown() {
    const sectionId = this.dataset.sectionId;
    try {
      sessionStorage.setItem(`popup-shown-${sectionId}`, 'true');
    } catch {
      // sessionStorage not available
    }
  }

  /**
   * Handles the "don't show again" checkbox change
   * @param {Event} event
   */
  handleDontShowAgain = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    // Don't save dismissal in edit mode
    if (this.dataset.editMode === 'true') return;

    const sectionId = this.dataset.sectionId;
    const daysToHide = parseInt(this.dataset.daysToHide, 10) || 30;

    if (event.target.checked) {
      try {
        const expiry = Date.now() + daysToHide * 24 * 60 * 60 * 1000;
        localStorage.setItem(
          `popup-dismissed-${sectionId}`,
          JSON.stringify({ expiry })
        );
      } catch {
        // localStorage not available
      }
    } else {
      try {
        localStorage.removeItem(`popup-dismissed-${sectionId}`);
      } catch {
        // localStorage not available
      }
    }
  };
}

if (!customElements.get('popup-section-component')) {
  customElements.define('popup-section-component', PopupSectionComponent);
}
